"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * parece-app — Tutorial de Hábitos stepper + local demos.
 *
 * Everything here is client-only demo state: no REST calls, nothing persisted.
 * Mirrors the interaction conventions of modules/habitos.ts (root-guard,
 * event delegation on the root, is-selected + aria-pressed toggling) without
 * touching that file.
 */
class HabitosTutorial {
    constructor() {
        this.root = null;
        this.steps = [];
        this.dots = [];
        this.current = 0;
        // Demo 1 (Paso 2 — revisión diaria): habit id -> answer.
        this.demoAnswers = new Map();
    }
    init() {
        this.root = document.querySelector("[data-habitos-tutorial]");
        if (!this.root)
            return;
        this.steps = Array.from(this.root.querySelectorAll("[data-tutorial-step]"));
        this.dots = Array.from(this.root.querySelectorAll("[data-tutorial-dot]"));
        if (!this.steps.length)
            return;
        this.root.addEventListener("click", (event) => this.onClick(event));
        this.root.addEventListener("mouseover", (event) => this.onHover(event));
        this.goToStep(0, { focus: false });
    }
    onClick(event) {
        const target = event.target;
        const dot = target.closest("[data-tutorial-dot]");
        if (dot) {
            const index = this.steps.findIndex((step) => step.dataset.tutorialStep === dot.dataset.tutorialDot);
            if (index !== -1)
                this.goToStep(index);
            return;
        }
        if (target.closest("[data-tutorial-next]")) {
            this.goToStep(this.current + 1);
            return;
        }
        if (target.closest("[data-tutorial-prev]")) {
            this.goToStep(this.current - 1);
            return;
        }
        const demoAnswer = target.closest("[data-tutorial-demo-answer]");
        if (demoAnswer) {
            this.setDemoAnswer(demoAnswer);
            return;
        }
        const demoDay = target.closest("[data-tutorial-demo-day]");
        if (demoDay) {
            this.showDemoDay(demoDay);
        }
    }
    onHover(event) {
        const target = event.target;
        const demoDay = target.closest("[data-tutorial-demo-day]");
        if (demoDay)
            this.showDemoDay(demoDay);
    }
    goToStep(index, options = {}) {
        const focus = options.focus !== false;
        const clamped = Math.max(0, Math.min(this.steps.length - 1, index));
        this.current = clamped;
        this.steps.forEach((step, stepIndex) => {
            step.hidden = stepIndex !== clamped;
        });
        this.dots.forEach((dot, dotIndex) => {
            const active = dotIndex === clamped;
            dot.classList.toggle("is-active", active);
            if (active) {
                dot.setAttribute("aria-current", "step");
            }
            else {
                dot.removeAttribute("aria-current");
            }
        });
        const prev = this.root.querySelector("[data-tutorial-prev]");
        const next = this.root.querySelector("[data-tutorial-next]");
        if (prev)
            prev.disabled = clamped === 0;
        if (next) {
            next.disabled = clamped === this.steps.length - 1;
            next.textContent =
                clamped === this.steps.length - 1 ? "Ya terminaste" : "Siguiente";
        }
        const label = this.root.querySelector("[data-tutorial-step-label]");
        if (label)
            label.textContent = `Paso ${clamped + 1} de ${this.steps.length}`;
        if (focus) {
            const heading = this.steps[clamped].querySelector("h2[tabindex]");
            heading === null || heading === void 0 ? void 0 : heading.focus();
        }
    }
    /** Paso 2 demo: toggle Sí/No on a fake habit row, then recompute the ring. */
    setDemoAnswer(button) {
        const habitId = button.dataset.tutorialDemoHabit;
        if (!habitId)
            return;
        const answer = button.dataset.tutorialDemoAnswer === "yes";
        this.demoAnswers.set(habitId, answer);
        const row = button.closest("[data-tutorial-demo-row]");
        row === null || row === void 0 ? void 0 : row.querySelectorAll("[data-tutorial-demo-answer]").forEach((candidate) => {
            const selected = (candidate.dataset.tutorialDemoAnswer === "yes") === answer;
            candidate.classList.toggle("is-selected", selected);
            candidate.setAttribute("aria-pressed", selected ? "true" : "false");
        });
        this.renderDemoRing();
    }
    renderDemoRing() {
        const demo = this.root.querySelector("[data-tutorial-demo-review]");
        if (!demo)
            return;
        const totalHabits = demo.querySelectorAll("[data-tutorial-demo-row]").length;
        const answered = this.demoAnswers.size;
        const complete = answered === totalHabits && totalHabits > 0;
        const yesCount = Array.from(this.demoAnswers.values()).filter(Boolean).length;
        const score = complete ? Math.round((yesCount / totalHabits) * 100) : 0;
        const ring = demo.querySelector("[data-tutorial-demo-ring]");
        if (ring) {
            ring.style.setProperty("--habitos-score", String(score));
            ring.setAttribute("aria-label", complete
                ? `${score} por ciento de balance`
                : `Revisión pendiente, ${answered} de ${totalHabits} revisados`);
        }
        const scoreNode = demo.querySelector("[data-tutorial-demo-score]");
        if (scoreNode)
            scoreNode.textContent = complete ? `${score}%` : "—";
        const labelNode = demo.querySelector("[data-tutorial-demo-ring-label]");
        if (labelNode)
            labelNode.textContent = complete
                ? "balance de hoy"
                : `${answered} de ${totalHabits} revisados`;
    }
    /** Paso 4 demo: show the tapped/hovered day's label. */
    showDemoDay(button) {
        const output = this.root.querySelector("[data-tutorial-demo-day-output]");
        if (!output)
            return;
        this.root
            .querySelectorAll("[data-tutorial-demo-day]")
            .forEach((day) => day.classList.remove("is-selected"));
        button.classList.add("is-selected");
        const label = button.dataset.tutorialDemoDayLabel || "";
        const level = button.dataset.level;
        const detail = level === "unreviewed"
            ? "sin revisión"
            : `${Number(level) * 25} % aproximado`;
        output.textContent = label ? `${label}: ${detail}` : detail;
    }
}
exports.default = HabitosTutorial;
